import type {
  MediumOnboardingFormState,
  MediumFinancialTransparencyData,
} from "gsintegrations";
import { axiosClient } from "../api/axiosClient";
import type {
  QuickMediumSignupPayload,
  QuickMediumSocialSignupPayload,
} from "../types";
import type { InventoryListQueryFilters } from "../hooks/useTerreiroInventoryListQuery";

/**
 * Represents a role assignment for a medium, as returned by the API.
 */
export interface MediumRoleAssignment {
  id: string;
  mediumId: string;
  roleId: string;
  startDate: string | null;
  endDate: string | null;
  role: {
    id: string;
    name: string;
    description: string | null;
  };
}

export interface MediumRankAssignment {
  id: string;
  mediumId: string;
  rankId: string;
  date: string;
  rank: {
    id: string;
    name: string;
    description: string | null;
    level: number;
  };
}

// extend the MediumOnboardingFormState to add code as string
export interface ExtendedMediumOnboardingFormState extends MediumOnboardingFormState {
  code: string;
}

const DEFAULTS: Partial<ExtendedMediumOnboardingFormState> = {
  complement: "",
  code: "",
};

export const mediumRepository = {
  async signup(
    payload: ExtendedMediumOnboardingFormState,
    attempt = 1,
  ): Promise<{ msg: string }> {
    try {
      const res = await axiosClient.post(`/medium-signup`, payload, {
        withCredentials: true,
      });
      return res.data;
    } catch (err: unknown) {
      interface AxiosErrorWithResponse {
        response?: {
          status?: number;
          data?: {
            error?: string;
            missing?: (keyof ExtendedMediumOnboardingFormState)[];
          };
        };
      }
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as AxiosErrorWithResponse).response === "object"
      ) {
        const response = (err as AxiosErrorWithResponse).response;
        if (
          response?.status === 400 &&
          response?.data?.error === "MISSING_FIELDS"
        ) {
          const missing = response.data
            .missing as (keyof ExtendedMediumOnboardingFormState)[];
          const supplemented: ExtendedMediumOnboardingFormState = {
            ...payload,
          };
          missing.forEach((field) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (supplemented as any)[field] = DEFAULTS[field] ?? "";
          });
          return await mediumRepository.signup(supplemented, attempt);
        }
        if (
          response?.status !== undefined &&
          response.status >= 500 &&
          attempt < 3
        ) {
          await new Promise((r) => setTimeout(r, 2 ** attempt * 500));
          return await mediumRepository.signup(payload, attempt + 1);
        }
      }
      throw err;
    }
  },

  /**
   * Quick signup - creates account with minimal required fields.
   * Uses /medium-signup/quick endpoint.
   */
  async quickSignup(
    payload: QuickMediumSignupPayload,
    attempt = 1,
  ): Promise<{ msg: string; profileComplete: boolean }> {
    try {
      const res = await axiosClient.post(`/medium-signup/quick`, payload, {
        withCredentials: true,
      });
      return res.data;
    } catch (err: unknown) {
      interface AxiosErrorWithResponse {
        response?: {
          status?: number;
        };
      }
      // Retry on 5xx errors
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as AxiosErrorWithResponse).response === "object"
      ) {
        const response = (err as AxiosErrorWithResponse).response;
        if (
          response?.status !== undefined &&
          response.status >= 500 &&
          attempt < 3
        ) {
          await new Promise((r) => setTimeout(r, 2 ** attempt * 500));
          return await mediumRepository.quickSignup(payload, attempt + 1);
        }
      }
      throw err;
    }
  },

  async quickSignupSocial(
    payload: QuickMediumSocialSignupPayload,
    attempt = 1,
  ): Promise<{ msg: string; profileComplete: boolean }> {
    try {
      const res = await axiosClient.post(
        `/medium-signup/quick/social`,
        payload,
        {
          withCredentials: true,
        },
      );
      return res.data;
    } catch (err: unknown) {
      interface AxiosErrorWithResponse {
        response?: {
          status?: number;
        };
      }
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as AxiosErrorWithResponse).response === "object"
      ) {
        const response = (err as AxiosErrorWithResponse).response;
        if (
          response?.status !== undefined &&
          response.status >= 500 &&
          attempt < 3
        ) {
          await new Promise((r) => setTimeout(r, 2 ** attempt * 500));
          return await mediumRepository.quickSignupSocial(payload, attempt + 1);
        }
      }
      throw err;
    }
  },

  async getMediumById(mediumId: string): Promise<unknown> {
    const res = await axiosClient.get(`/medium/${mediumId}`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getMyStatus(mediumId?: string): Promise<unknown> {
    const params = mediumId ? `?mediumId=${mediumId}` : "";
    const res = await axiosClient.get(`/medium/me/status${params}`, {
      withCredentials: true,
    });
    return res.data;
  },

  async getMyMediumData(): Promise<unknown> {
    const res = await axiosClient.get(`/medium/me`, {
      withCredentials: true,
    });
    return res.data;
  },

  async exportMyHistoryXml(input: {
    terreiroId: string;
    period?: string;
    month?: number;
    year?: number;
  }): Promise<Blob> {
    const res = await axiosClient.get(`/medium/me/history/export`, {
      withCredentials: true,
      params: {
        terreiroId: input.terreiroId,
        ...(input.period ? { period: input.period } : {}),
        ...(typeof input.month === "number" ? { month: input.month } : {}),
        ...(typeof input.year === "number" ? { year: input.year } : {}),
      },
      responseType: "blob",
    });

    return res.data;
  },

  async exportMediumHistoryXmlByMediumId(input: {
    mediumId: string;
    period?: string;
    month?: number;
    year?: number;
  }): Promise<Blob> {
    const res = await axiosClient.get(
      `/medium/${input.mediumId}/history/export`,
      {
        withCredentials: true,
        params: {
          ...(input.period ? { period: input.period } : {}),
          ...(typeof input.month === "number" ? { month: input.month } : {}),
          ...(typeof input.year === "number" ? { year: input.year } : {}),
        },
        responseType: "blob",
      },
    );

    return res.data;
  },

  async getInventoryItemsByMedium(
    mediumId: string,
    filters?: InventoryListQueryFilters,
  ): Promise<unknown[]> {
    const res = await axiosClient.get(`/inventory-item`, {
      withCredentials: true,
      params: {
        mediumId,
        ...(filters?.name ? { name: filters.name } : {}),
        ...(filters?.status ? { status: filters.status } : {}),
        sortBy: "createdAt",
        sortOrder: filters?.sortOrder ?? "desc",
      },
    });
    return res.data;
  },
  async getBankAccountsByMedium(mediumId: string): Promise<unknown[]> {
    const res = await axiosClient.get(`/bank-account?mediumId=${mediumId}`, {
      withCredentials: true,
    });
    return res.data;
  },
  async updateMedium(
    mediumId: string,
    payload: Partial<ExtendedMediumOnboardingFormState>,
  ): Promise<unknown> {
    const res = await axiosClient.put(`/medium/${mediumId}`, payload, {
      withCredentials: true,
    });
    return res.data;
  },

  /**
   * Get financial transparency data for a medium.
   * Shows shared expenses and the medium's payment status.
   */
  async getFinancialTransparency(
    mediumId: string,
  ): Promise<MediumFinancialTransparencyData> {
    const res = await axiosClient.get<MediumFinancialTransparencyData>(
      `/medium/${mediumId}/financial-transparency`,
      {
        withCredentials: true,
      },
    );
    return res.data;
  },

  /**
   * Get all roles assigned to a medium.
   * Returns array of role objects with id, name, description, startDate, endDate.
   */
  async getMediumRoles(mediumId: string): Promise<MediumRoleAssignment[]> {
    const res = await axiosClient.get<MediumRoleAssignment[]>(
      `/medium/${mediumId}/roles`,
      { withCredentials: true },
    );
    return res.data;
  },

  /**
   * Replace the current rank for a medium.
   * The backend removes the previous assignment and persists the new one atomically.
   */
  async replaceMediumRanks(
    mediumId: string,
    rankId: string | null,
    date?: string,
  ): Promise<MediumRankAssignment[]> {
    const res = await axiosClient.put<MediumRankAssignment[]>(
      `/medium/${mediumId}/ranks`,
      { rankId, date },
      { withCredentials: true },
    );
    return res.data;
  },

  /**
   * Replace all roles for a medium (diff-based batch assignment).
   * The backend calculates adds/removes and applies them in a transaction.
   */
  async replaceMediumRoles(
    mediumId: string,
    roleIds: string[],
    startDate?: string,
  ): Promise<MediumRoleAssignment[]> {
    const res = await axiosClient.put<MediumRoleAssignment[]>(
      `/medium/${mediumId}/roles`,
      { roleIds, startDate },
      { withCredentials: true },
    );
    return res.data;
  },
};
